import { Inject, Injectable } from "@angular/core";
import { MobileAuthResult } from "../models/mobile-auth.models";
import { IdentityProfilData, TenantAppMobileSummary } from "../models/mobile-profile.models";
import { IKeyValueStore, MOBILE_KEY_VALUE_STORE } from "./key-value-store.interface";

const CACHE_KEYS = {
  profile: "mobile:profile",
  authResult: "mobile:auth-result",
  availableApps: "mobile:available-apps",
  activeTenantAppUid: "mobile:active-tenant-app-uid",
} as const;

@Injectable({ providedIn: "root" })
export class MobileAppCacheService {
  constructor(@Inject(MOBILE_KEY_VALUE_STORE) private readonly keyValueStore: IKeyValueStore) {}

  async saveProfile(profile: IdentityProfilData): Promise<void> {
    await this.keyValueStore.set(CACHE_KEYS.profile, profile);
  }

  async readProfile(): Promise<IdentityProfilData | null> {
    return this.keyValueStore.get<IdentityProfilData>(CACHE_KEYS.profile);
  }

  async saveAuthResult(authResult: MobileAuthResult): Promise<void> {
    await this.keyValueStore.set(CACHE_KEYS.authResult, authResult);
  }

  async readAuthResult(): Promise<MobileAuthResult | null> {
    return this.keyValueStore.get<MobileAuthResult>(CACHE_KEYS.authResult);
  }

  async saveAvailableApps(apps: TenantAppMobileSummary[]): Promise<void> {
    await this.keyValueStore.set(CACHE_KEYS.availableApps, apps);
  }

  async readAvailableApps(): Promise<TenantAppMobileSummary[]> {
    return (await this.keyValueStore.get<TenantAppMobileSummary[]>(CACHE_KEYS.availableApps)) ?? [];
  }

  async saveActiveTenantAppUid(tenantAppUid: string | null): Promise<void> {
    if (tenantAppUid) {
      await this.keyValueStore.set(CACHE_KEYS.activeTenantAppUid, tenantAppUid);
    } else {
      await this.keyValueStore.remove(CACHE_KEYS.activeTenantAppUid);
    }
  }

  async readActiveTenantAppUid(): Promise<string | null> {
    return (await this.keyValueStore.get<string>(CACHE_KEYS.activeTenantAppUid)) ?? null;
  }

  async clearAll(): Promise<void> {
    await Promise.all([
      this.keyValueStore.remove(CACHE_KEYS.profile),
      this.keyValueStore.remove(CACHE_KEYS.authResult),
      this.keyValueStore.remove(CACHE_KEYS.availableApps),
      this.keyValueStore.remove(CACHE_KEYS.activeTenantAppUid),
    ]);
  }
}
